import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  Home,
  FolderOpen,
  User,
  Users,
  Briefcase,
  MessageSquare,
  Plus,
  LogOut,
  Settings,
  BarChart3,
  Download,
  DollarSign,
  Bell,
  Video,
  Upload,
  Menu,
  X,
  Star,
  Target,
  Moon,
  Sun,
  Search,
  Zap,
  Shield,
  FileSignature,
  LifeBuoy,
  ClipboardList,
  Gift,
  Award,
  PhoneCall
} from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { useQuery } from 'react-query';
import axios from 'axios';
import { getApiUrl } from '../../utils/api';

const DashboardLayout = ({ children }) => {
  const { user, logout, isAdmin, isTeamMember, isManager, isDeveloper } = useAuth();
  const { isDarkMode, toggleTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [logoError, setLogoError] = useState(false);

  const { data: settingsData, isLoading: isSettingsLoading } = useQuery(
    'global-settings',
    () => axios.get('/api/settings').then(res => res.data.data),
    { 
      refetchOnWindowFocus: true, 
      staleTime: 1000,
      initialData: () => {
        const cached = localStorage.getItem('epic-edge-settings');
        return cached ? JSON.parse(cached) : undefined;
      }
    }
  );

  // Sync to localStorage whenever data changes
  React.useEffect(() => {
    if (settingsData) {
      localStorage.setItem('epic-edge-settings', JSON.stringify(settingsData));
    }
  }, [settingsData]);

  // Reset logo error when settings change
  React.useEffect(() => {
    setLogoError(false);
  }, [settingsData?.adminLogoUrl]);

  const resolveLogoUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('http') || url.startsWith('data:')) return url;
    return getApiUrl(url);
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  // Base navigation for all users
  const baseNavigation = [
    {
      name: 'Dashboard',
      href: '/dashboard',
      icon: Home,
      current: location.pathname === '/dashboard'
    },
    {
      name: 'Profile',
      href: '/profile',
      icon: User,
      current: location.pathname === '/profile'
    }
  ];

  // Client-only main navigation
  const clientMainNavigation = [
    {
      name: 'My Projects',
      href: '/projects',
      icon: FolderOpen,
      current: location.pathname === '/projects' || location.pathname.startsWith('/projects/')
    }
  ];

  // Combine navigation based on user role
  const navigation = isTeamMember
    ? baseNavigation
    : [...baseNavigation.slice(0, 1), ...clientMainNavigation, ...baseNavigation.slice(1)];

  // Client-specific navigation for non-admin users
  const clientNavigation = [
    {
      name: 'Messages',
      href: '/messages',
      icon: MessageSquare,
      current: location.pathname === '/messages',
      disabled: false
    },
    {
      name: 'Contracts & MSAs',
      href: '/contracts',
      icon: FileSignature,
      current: location.pathname === '/contracts',
      disabled: false
    },
    {
      name: 'Support Tickets',
      href: '/tickets',
      icon: LifeBuoy,
      current: location.pathname === '/tickets' || location.pathname.startsWith('/tickets/'),
      disabled: false
    },
    {
      name: 'Onboarding Forms',
      href: '/forms',
      icon: ClipboardList,
      current: location.pathname === '/forms' || location.pathname.startsWith('/forms/'),
      disabled: false
    },
    {
      name: 'Billing',
      href: '/billing',
      icon: DollarSign,
      current: location.pathname === '/billing',
      disabled: false
    },
    {
      name: 'Notifications',
      href: '/notifications',
      icon: Bell,
      current: location.pathname === '/notifications',
      disabled: false
    },
    {
      name: 'Feedback',
      href: '/feedback',
      icon: Star,
      current: location.pathname === '/feedback',
      disabled: false
    },
    {
      name: 'Refer & Earn',
      href: '/referral-rewards',
      icon: Gift,
      current: location.pathname === '/referral-rewards',
      disabled: false
    }
  ];

  const adminNavigation = [
    ...(isAdmin ? [
      {
        name: 'Team Management',
        href: '/admin-team',
        icon: Users,
        current: location.pathname === '/admin-team'
      },
      {
        name: 'Users',
        href: '/admin-users',
        icon: User,
        current: location.pathname === '/admin-users'
      },
      {
        name: 'Referrals & Affiliates',
        href: '/admin-referrals',
        icon: Award,
        current: location.pathname === '/admin-referrals'
      }
    ] : []),
    {
      name: 'All Projects',
      href: '/admin-projects',
      icon: Briefcase,
      current: location.pathname === '/admin-projects'
    },
    {
      name: 'Live Mirror Hub',
      href: '/admin-mirrors',
      icon: Zap,
      current: location.pathname === '/admin-mirrors'
    },
    {
      name: 'Security Vault',
      href: '/admin-security',
      icon: Shield,
      current: location.pathname === '/admin-security'
    },
    {
      name: 'Client Messages',
      href: '/admin-messages',
      icon: MessageSquare,
      current: location.pathname === '/admin-messages'
    },
    {
      name: 'Call Logs & Intercom',
      href: '/admin-calls',
      icon: PhoneCall,
      current: location.pathname === '/admin-calls'
    },
    {
      name: 'Support Helpdesk',
      href: '/admin-tickets',
      icon: LifeBuoy,
      current: location.pathname === '/admin-tickets'
    },
    {
      name: 'Intake Forms',
      href: '/admin-forms',
      icon: ClipboardList,
      current: location.pathname === '/admin-forms'
    },
    ...(isAdmin ? [
      {
        name: 'Send Notifications',
        href: '/admin-notifications',
        icon: Bell,
        current: location.pathname === '/admin-notifications'
      }
    ] : []),
    {
      name: 'Manage Feedback',
      href: '/admin-feedback',
      icon: Star,
      current: location.pathname === '/admin-feedback'
    },
    {
      name: 'Contact Forms',
      href: '/admin-contacts',
      icon: Bell,
      current: location.pathname === '/admin-contacts'
    },
    ...(isAdmin ? [
      {
        name: 'Billing Management',
        href: '/admin-billing',
        icon: DollarSign,
        current: location.pathname === '/admin-billing'
      }
    ] : []),
    ...((isAdmin || isManager) ? [
      {
        name: 'Portfolio',
        href: '/admin-portfolio',
        icon: BarChart3,
        current: location.pathname === '/admin-portfolio'
      }
    ] : []),
    ...(isAdmin ? [
      {
        name: 'Platform Settings',
        href: '/admin-settings',
        icon: Settings,
        current: location.pathname === '/admin-settings'
      }
    ] : []),
    {
      name: 'Contracts Tracker',
      href: '/admin-contracts',
      icon: FileSignature,
      current: location.pathname === '/admin-contracts'
    },
    {
      name: 'AI Lead Intelligence',
      href: '/admin-ai',
      icon: Target,
      current: location.pathname === '/admin-ai'
    },
    {
      name: 'AI Proposals',
      href: '/admin-proposals',
      icon: FolderOpen,
      current: location.pathname === '/admin-proposals' || location.pathname.startsWith('/admin-proposals/')
    },
    ...((isAdmin || isManager) ? [
      {
        name: 'Create Project',
        href: '/create-project',
        icon: Plus,
        current: location.pathname === '/create-project'
      }
    ] : [])
  ];

  const filteredAdminNavigation = adminNavigation.filter(item => {
    if (user?.role === 'marketing') {
      const forbidden = ['Live Mirror Hub', 'Security Vault'];
      return !forbidden.includes(item.name);
    }
    return true;
  });

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-onyx-950 transition-colors duration-300">
      {/* Mobile menu overlay */}
      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 z-40 bg-black bg-opacity-50 lg:hidden"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div className={`fixed inset-y-0 left-0 w-64 bg-white dark:bg-onyx-900 shadow-xl border-r border-gray-200 dark:border-onyx-800 transform transition-transform duration-300 ease-in-out z-50 flex flex-col ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
        } lg:translate-x-0`}>
        {/* Logo */}
        <div className={`flex items-center h-16 px-6 transition-colors duration-300 ${
          settingsData?.adminLogoUrl ? 'bg-white dark:bg-onyx-900' : 'bg-gradient-to-r from-amber-500 to-yellow-600'
        }`}>
          <div className="flex items-center">
            {(settingsData?.adminLogoUrl && !logoError) ? (
              <img 
                src={resolveLogoUrl(settingsData.adminLogoUrl)} 
                alt="Admin Logo" 
                className="h-10 w-auto object-contain"
                onError={() => setLogoError(true)}
              />
            ) : null}
            
            {/* Show default logo if: 1. Loading finished AND no URL exists OR 2. The URL exists but the image failed to load */}
            {(!isSettingsLoading && (!settingsData?.adminLogoUrl || logoError)) && (
              <div className="flex items-center">
                <div className="w-8 h-8 bg-white bg-opacity-20 rounded-lg flex items-center justify-center">
                  <span className="text-white font-bold text-sm">E</span>
                </div>
                <div className="ml-3">
                  <h1 className="text-white font-semibold text-lg">EpicEdge</h1>
                  <p className="text-amber-100 text-xs">Creative System</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Navigation */}
        <nav className="mt-4 px-4 flex-1 overflow-y-auto">
          <div className="space-y-2">
            {navigation.map((item) => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={closeMobileMenu}
                  className={`flex items-center px-4 py-3 rounded-lg text-sm font-medium transition-colors ${item.current
                    ? 'bg-gradient-to-r from-amber-500 to-yellow-600 text-white shadow-lg'
                    : 'text-gray-700 dark:text-gray-300 hover:bg-amber-50 dark:hover:bg-onyx-800 hover:text-amber-700'
                    }`}
                >
                  <Icon className="w-5 h-5 mr-3" />
                  {item.name}
                </Link>
              );
            })}
          </div>

          {/* Client Section */}
          {!isTeamMember && (
            <div className="mt-8">
              <div className="px-4 py-2">
                <h3 className="text-xs font-semibold text-amber-600 uppercase tracking-wider">
                  Client Tools
                </h3>
              </div>
              <div className="space-y-2">
                {clientNavigation.map((item) => {
                  const Icon = item.icon;
                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      onClick={closeMobileMenu}
                      className={`flex items-center px-4 py-3 rounded-lg text-sm font-medium transition-colors ${item.current
                        ? 'bg-gradient-to-r from-amber-500 to-yellow-600 text-white shadow-lg'
                        : 'text-gray-700 dark:text-gray-300 hover:bg-amber-50 dark:hover:bg-onyx-800 hover:text-amber-700 dark:hover:text-amber-500'
                        }`}
                    >
                      <Icon className="w-5 h-5 mr-3" />
                      {item.name}
                    </Link>
                  );
                })}
              </div>
            </div>
          )}

          {/* Team Section */}
          {isTeamMember && (
            <div className="mt-8">
              <div className="px-4 py-2">
                <h3 className="text-xs font-semibold text-amber-600 uppercase tracking-wider">
                  {isAdmin ? 'Administration' : 'Team Area'}
                </h3>
              </div>
              <div className="space-y-2">
                {filteredAdminNavigation.map((item) => {
                  const Icon = item.icon;
                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      onClick={closeMobileMenu}
                      className={`flex items-center px-4 py-3 rounded-lg text-sm font-medium transition-colors ${item.current
                        ? 'bg-gradient-to-r from-amber-500 to-yellow-600 text-white shadow-lg'
                        : 'text-gray-700 dark:text-gray-300 hover:bg-amber-50 dark:hover:bg-onyx-800 hover:text-amber-700 dark:hover:text-amber-500'
                        }`}
                    >
                      <Icon className="w-5 h-5 mr-3" />
                      {item.name}
                    </Link>
                  );
                })}
              </div>
            </div>
          )}
        </nav>

        {/* User Profile & Logout */}
        <div className="flex-shrink-0 p-4 bg-gray-50 dark:bg-onyx-950/50 border-t border-gray-200 dark:border-onyx-800">
          <div className="flex items-center mb-4">
            <div className="w-10 h-10 bg-gradient-to-r from-amber-500 to-yellow-600 rounded-full flex items-center justify-center">
              <span className="text-white font-semibold text-sm">
                {user?.name?.charAt(0)?.toUpperCase() || 'U'}
              </span>
            </div>
            <div className="ml-3 flex-1 overflow-hidden">
              <p className="text-gray-900 dark:text-gray-100 text-sm font-medium truncate">{user?.name}</p>
              <p className="text-gray-600 dark:text-gray-400 text-xs truncate">{user?.email}</p>
            </div>
          </div>

          <button
            onClick={handleLogout}
            className="w-full flex items-center px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-red-50 dark:hover:bg-red-900/10 hover:text-red-700 dark:hover:text-red-400 rounded-lg transition-colors border border-gray-200 dark:border-onyx-700"
          >
            <LogOut className="w-4 h-4 mr-3" />
            Sign Out
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="lg:ml-64">
        {/* Top Header */}
        <header className="bg-white dark:bg-onyx-900 shadow-sm border-b border-gray-200 dark:border-onyx-800 transition-colors duration-300">
          <div className="px-4 sm:px-6 py-4">
            <div className="flex items-center justify-between">
              {/* Mobile menu button */}
              <div className="flex items-center">
                <button
                  onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                  className="lg:hidden p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-amber-500"
                >
                  {isMobileMenuOpen ? (
                    <X className="w-6 h-6" />
                  ) : (
                    <Menu className="w-6 h-6" />
                  )}
                </button>
                <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white ml-2 lg:ml-0">
                  {location.pathname === '/dashboard' && 'Dashboard'}
                  {location.pathname === '/projects' && 'My Projects'}
                  {location.pathname === '/profile' && 'Profile Settings'}
                  {location.pathname === '/messages' && 'Messages'}
                  {location.pathname === '/contracts' && 'My Contracts & MSAs'}
                  {location.pathname === '/tickets' && 'Support Tickets'}
                  {location.pathname.startsWith('/tickets/') && 'Ticket Details'}
                  {location.pathname === '/forms' && 'Onboarding Questionnaires'}
                  {location.pathname.startsWith('/forms/') && 'Complete Form'}
                  {location.pathname === '/billing' && 'Billing & Invoices'}
                  {location.pathname === '/notifications' && 'Notifications'}
                  {location.pathname === '/feedback' && 'Feedback & Reviews'}
                  {location.pathname === '/admin-team' && 'Team Management'}
                  {location.pathname === '/admin-users' && 'Manage Users'}
                  {location.pathname === '/admin-projects' && 'All Projects'}
                  {location.pathname === '/admin-portfolio' && 'Portfolio Management'}
                  {location.pathname === '/admin-settings' && 'Platform Settings'}
                  {location.pathname === '/admin-ai' && 'Lead Intelligence (AI)'}
                  {location.pathname.startsWith('/admin-proposals') && 'AI-Crafted Proposals'}
                  {location.pathname === '/admin-contacts' && 'Contact Forms'}
                  {location.pathname === '/admin-messages' && 'Client Messages'}
                  {location.pathname === '/admin-contracts' && 'Contracts Tracker'}
                  {location.pathname === '/admin-tickets' && 'Support Helpdesk'}
                  {location.pathname === '/admin-forms' && 'Intake Forms & Briefs'}
                  {location.pathname === '/admin-notifications' && 'Send Notifications'}
                  {location.pathname === '/admin-feedback' && 'Manage Feedback'}
                  {location.pathname === '/create-project' && 'Create New Project'}
                  {location.pathname.startsWith('/edit-project') && 'Edit Project'}
                  {location.pathname.startsWith('/projects/') && 'Project Details'}
                </h1>
              </div>

              <div className="flex items-center space-x-2 sm:space-x-4">
                <button
                  onClick={toggleTheme}
                  className="p-2 text-gray-500 dark:text-gray-400 hover:text-amber-600 dark:hover:text-amber-500 hover:bg-amber-50 dark:hover:bg-onyx-800 rounded-lg transition-all duration-200"
                  title={isDarkMode ? 'Switch to Light Mode' : 'Switch to Onyx Mode'}
                >
                  {isDarkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
                </button>

                <button
                  onClick={() => window.dispatchEvent(new Event('open_command_center'))}
                  className="p-2 text-gray-500 dark:text-gray-400 hover:text-amber-600 dark:hover:text-amber-500 hover:bg-amber-50 dark:hover:bg-onyx-800 rounded-lg transition-all duration-200"
                  title="Search (Ctrl+K)"
                >
                  <Search className="w-5 h-5" />
                </button>
                <span className="hidden sm:block text-sm text-gray-500 dark:text-gray-400">
                  Welcome back, {user?.name}
                </span>
                <Link
                  to="/"
                  className="text-xs sm:text-sm text-amber-600 hover:text-amber-500 font-medium px-2 sm:px-3 py-1 rounded-lg border border-amber-200 dark:border-onyx-700 hover:bg-amber-50 dark:hover:bg-onyx-800 transition-colors"
                >
                  <span className="hidden sm:inline">← Back to Website</span>
                  <span className="sm:hidden">← Website</span>
                </Link>
              </div>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-4 sm:p-6 max-w-6xl mx-auto">
          {children}
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;
